import React from "react";
import { SegmentView, SuppressionView } from "@tiny/api";
import {
  useDeleteSuppressionMutation,
  usePutSuppressionFindFilterQuery,
} from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import { useRefCallBack } from "@tiny/components/src/hooks/useRefCallBack";
import useSelectable from "@tiny/components/src/hooks/useSelectable";
import { SuppressionListProps } from "./SuppressionList.types";
import { SortOption } from "./SuppressionListFilter/SuppressionListFilter.types";
import useLocalStorage from "@tiny/components/src/hooks/useLocalStorage";
import { useSuppressionStatistics } from "../../hooks/useSuppressionStatistics";

export const sortOptions: SortOption[] = [
  { label: "Name", value: ["name,ASC"] },
  { label: "Number of Customers", value: ["suppressedCount,DESC"] },
  { label: "Modified Date", value: ["updatedAt,DESC"], action: "Modified" },
  { label: "Created Date", value: ["createdAt,DESC"], action: "Created" },
];

const itemsPerPageOptions = [5, 10, 15, 20];

export function useSuppressionList(props: SuppressionListProps) {
  const {
    enableBulkDelete = false,
    enableSelectionActions = true,
    suppressionTypes = ["UPLOAD", "INTEGRATION"],
    suppressionScopes,
    onAfterDelete,
    selectionDisabled,
    selected: selectedProp,
    setSelected: setSelectedProp,
    visible = true,
    canToggleScope,
    isCampaignMode,
    // consumed by rows (not a DOM attr) — keep out of boxProps (spread onto <Box>).
    seeAllSubcribersOnUpload,
    ...boxProps
  } = props;

  const { deselectAll, selected, toggleSelect, isSelected } =
    useSelectable<SuppressionView>({
      idKey: "id",
      selected: selectedProp,
      setSelected: setSelectedProp,
    });

  const [storeSuppressionPageSize, setStoreSuppressionPageSize] =
    useLocalStorage("audienceListPageSize", itemsPerPageOptions[0]);

  const { refetchSuppressionStats } = useSuppressionStatistics();

  const [searchKeyword, setSearchKeyword] = React.useState("");
  const searchTextName = useDebounce(searchKeyword);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(storeSuppressionPageSize);
  const [sortOption, setSortOption] = React.useState(sortOptions[2]);
  const countAllCustomers = React.useMemo(() => false, []);

  const onPageChange = React.useCallback((page: number) => {
    setPage(page);
  }, []);

  const onItemsPerPageChange = React.useCallback(
    (size: number) => {
      setSize(size);
      setStoreSuppressionPageSize(size);
    },
    [setStoreSuppressionPageSize]
  );

  const isItemSelected = React.useCallback(
    (item: SuppressionView) => {
      return isSelected(item);
    },
    [isSelected]
  );

  const selectItem = React.useCallback(
    (item: SegmentView) => {
      toggleSelect(item, true);
    },
    [toggleSelect]
  );

  const unselectItem = React.useCallback(
    (item: SegmentView) => {
      toggleSelect(item, false);
    },
    [toggleSelect]
  );

  const unselectAll = React.useCallback(() => {
    deselectAll();
  }, [deselectAll]);

  const nrOfSelectedSuppressions = React.useMemo(
    () => selected.length,
    [selected]
  );

  const toggleSelectItem = React.useCallback(
    (item: SegmentView) => {
      isItemSelected(item) ? unselectItem(item) : selectItem(item);
    },
    [isItemSelected, selectItem, unselectItem]
  );

  React.useLayoutEffect(() => {
    setPage(0);
  }, [searchTextName, size, sortOption]);

  const {
    data: segmentData,
    refetch: refetchSuppressionList,
    isFetching: isSegmentLoading,
  } = usePutSuppressionFindFilterQuery(
    { searchTextName, suppressionScopes: suppressionScopes },
    { page, size, sort: sortOption.value, countAllCustomers },
    {
      keepPreviousData: true,
    }
  );

  useRefreshRegister(async () => {
    await refetchSuppressionList();
    await refetchSuppressionStats();
  });

  const reloadSuppressionList = useRefCallBack(
    React.useCallback(() => {
      refetchSuppressionList();
      refetchSuppressionStats();
    }, [refetchSuppressionList, refetchSuppressionStats])
  );

  const deleteSuppressionsMutation = useDeleteSuppressionMutation();

  const onDeleteSuppression = React.useCallback(
    async (suppression: SuppressionView) => {
      suppression.id &&
        (await deleteSuppressionsMutation.mutateAsync(
          { data: { ids: [suppression.id] } },
          {
            onSuccess: () => {
              unselectItem(suppression);
              reloadSuppressionList();
              onAfterDelete?.();
            },
          }
        ));
    },
    [
      deleteSuppressionsMutation,
      onAfterDelete,
      reloadSuppressionList,
      unselectItem,
    ]
  );

  const onDeleteSuppressions = React.useCallback(() => {
    const ids = selected.map((item) => item.id!);
    ids.length > 0 &&
      deleteSuppressionsMutation.mutateAsync(
        { data: { ids } },
        {
          onSuccess: () => {
            unselectAll();
            reloadSuppressionList();
            onAfterDelete?.();
          },
        }
      );
  }, [
    deleteSuppressionsMutation,
    onAfterDelete,
    reloadSuppressionList,
    selected,
    unselectAll,
  ]);

  const isSuppressionDeletable = React.useCallback(
    (suppression: SuppressionView) => true,
    []
  );

  const audiences = segmentData;
  const isEmpty = !isSegmentLoading && segmentData?.content?.length === 0;

  return {
    isSegmentLoading,
    audiences,
    boxProps,
    enableBulkDelete,
    enableSelectionActions,
    isSuppressionDeletable,
    isEmpty,
    isItemSelected,
    itemsPerPage: size,
    itemsPerPageOptions,
    nrOfSelectedSuppressions,
    onDeleteSuppression,
    onDeleteSuppressions,
    onItemsPerPageChange,
    onPageChange,
    page,
    reloadSuppressionList,
    searchKeyword,
    selectItem,
    selectionDisabled,
    setSearchKeyword,
    setSortOption,
    sortOption,
    sortOptions,
    toggleSelectItem,
    unselectAll,
    unselectItem,
    visible,
    canToggleScope,
    isCampaignMode,
  };
}
